<template>
  <div class="product-gallery-modal">
    <button
      class="product-gallery-modal__close-button icon-button overlay__close"
      type="button"
      :aria-label="$string('accessibility.close')"
      @click.prevent="handleCloseClick"
    >
      <slot name="close">
        <icon-close />
      </slot>
    </button>

    <div
      v-if="current3dModel"
      class="product-gallery__controls"
    >
      <button
        class="product-gallery__controls__button mobile-hidden"
        :class="{'product-gallery__controls__button--active': mode === 'zoom'}"
        type="button"
        @click.prevent="handleGalleryToggle"
        v-text="$string('product.gallery.gallery')"
      />
      <button
        class="product-gallery__controls__button"
        :class="{'product-gallery__controls__button--active': mode === '3d'}"
        type="button"
        @click.prevent="handle3dModelToggle"
        v-text="$string('product.gallery.view_model')"
      />
      <button
        v-if="current3dModel"
        class="product-gallery__controls__button product-gallery__controls__button-ar"
        data-shopify-xr
        data-shopify-xr-hidden
        type="button"
        :data-shopify-model3d-id="current3dModel?.id"
        :data-shopify-title="variant?.title"
        @click.prevent="triggerAR"
        v-text="$string('product.gallery.ar')"
      />
      <button
        v-if="current3dModel"
        class="product-gallery__controls__button product-gallery__controls__button-ar-tab"
        :class="{'product-gallery__controls__button--active': mode === 'ar-tab'}"
        type="button"
        @click.prevent="handleArTabToggle"
        v-text="$string('product.gallery.ar')"
      />
    </div>

    <transition name="fade">
      <product-gallery-zoom
        v-if="getComponentVisible('product-gallery-zoom') && mode === 'zoom'"
        v-cloak
        :current-index="currentIndex"
        :images="images"
        :modal-active="isActive"
        @gallery-closed="$emit('gallery-closed')"
        @update-gallery-index="handleZoomGalleryIndexUpdate"
      />
    </transition>

    <transition name="fade">
      <product-gallery-3-d
        v-if="getComponentVisible('product-gallery-zoom') && mode === '3d'"
        v-cloak
        :current-3d-model-markup="current3dModelMarkup"
        @gallery-closed="$emit('gallery-closed')"
      />
    </transition>

    <transition name="fade">
      <product-gallery-ar-tab
        v-if="getComponentVisible('product-gallery-zoom') && mode === 'ar-tab'"
        v-cloak
        :model-id="current3dModel.id"
        @gallery-closed="$emit('gallery-closed')"
      />
    </transition>
  </div>
</template>

<script>

/**
 * Vue: Product gallery modal (product-gallery-modal)
 * -----------------------------------------------------------------------------
 * Product Gallery overlay housing 'Zoomed' state.
 * @param {Number} currentIndex - default/current images index for the carousel.
 * @param {Boolean} isActive - Overlay drawer is active.
 * @param {Array} [images] - Array of images from a formatted product object.
 * @param {String} mode - zoom.
 */
import { defineAsyncComponent } from 'vue'
import { mapActions, mapState } from 'vuex'

import IconClose from '~icons/misc/cross.svg'
import ProductGallery3D from '~async/product-gallery-3d/product-gallery-3d'

export default {
  name: 'ProductGalleryModal',

  components: {
    IconClose,
    ProductGallery3D,
    ProductGalleryZoom: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.product-gallery-zoom' */
        '~async/product-gallery-zoom/product-gallery-zoom'
      ),
    }),
    ProductGalleryArTab: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.product-gallery-ar-tab' */
        '~async/product-gallery-ar-tab/product-gallery-ar-tab'
      ),
    }),
  },

  props: {
    currentIndex: {
      type: Number,
      default: 0,
    },
    images: {
      type: [Boolean, Array],
      default: false,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'zoom',
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    current3dModelMarkup: {
      type: [Boolean, String],
      default: false,
    },
    current3dModel: {
      type: [Object, Boolean],
      default: () => ({}),
    },
  },

  emits: {
    'gallery-closed': () => true,
    'update-gallery-index': () => true,
    'toggle-gallery': () => true,
    'toggle-3d-model': () => true,
    'toggle-ar-tab': () => true,
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      breakpoint: (state) => state.index.screen.breakpoint,
    }),
  },

  watch: {

    /**
     * Watch breakpoint to close on change.
     */
    breakpoint() {
      if (this.breakpoint === 'm') {
        this.closeOverlay('productGalleryModal')
      }
    },
  },

  mounted() {
    this.loadComponent('product-gallery-zoom')
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      loadComponent: 'components/load',
    }),

    /**
     * Events for when the 'Close' button is clicked.
     */
    handleCloseClick() {
      this.closeOverlay('productGalleryModal')
    },

    /**
     * The user has modified the active slide in the 'Zoom modal'.
     * - Notify parent component.
     */
    handleZoomGalleryIndexUpdate(index) {
      this.$emit('update-gallery-index', index)
    },

    handleGalleryToggle () {
      this.$emit('toggle-gallery')
    },

    handle3dModelToggle () {
      this.$emit('toggle-3d-model')
    },

    handleArTabToggle () {
      this.$emit('toggle-ar-tab')
    },

    triggerAR() {
      if(!this.current3dModel) {
        return
      }

      window.ShopifyXR.launchXR({
        model3dId: this.current3dModel.id,
        title: this.variant.title,
      })
    },
  },
}
</script>

<style lang="scss">
@import './product-gallery-modal';
</style>
